/**
 * PresetSelector - Quick game preset dropdown
 *
 * Allows host to quickly select game length presets (Quick/Normal/Long/Epic).
 */

import { useEffect } from "react";
import { useSettingsStore } from "../../store/settingsStore";
import type { GamePreset, LobbySettings } from "../../types/game";

interface PresetSelectorProps {
  gameId: string;
  currentSettings: LobbySettings | null;
  onPresetSelect: (presetName: string) => void;
  disabled?: boolean;
}

export function PresetSelector({
  gameId,
  currentSettings,
  onPresetSelect,
  disabled = false,
}: PresetSelectorProps) {
  const { availablePresets, fetchPresets } = useSettingsStore();

  // Fetch presets on mount or when gameId changes
  useEffect(() => {
    if (gameId) {
      fetchPresets(gameId);
    }
  }, [gameId, fetchPresets]);

  // Determine which preset is currently active (if any)
  const getActivePreset = (): string | null => {
    if (!currentSettings) return null;

    for (const preset of availablePresets) {
      const matches = Object.keys(preset.settings).every(
        (key) => currentSettings[key] === preset.settings[key],
      );
      if (matches) return preset.name;
    }
    return null;
  };

  const activePreset = getActivePreset();

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const presetName = e.target.value;
    if (presetName && !disabled) {
      onPresetSelect(presetName);
    }
  };

  if (availablePresets.length === 0) {
    return null; // Don't render if no presets available
  }

  return (
    <div className="mb-4" data-testid="preset-selector">
      <label htmlFor="preset-select" className="block text-sm font-medium mb-2">
        Game Length
      </label>
      <select
        id="preset-select"
        value={activePreset || ""}
        onChange={handleChange}
        disabled={disabled}
        className={`w-full px-3 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-purple-500 ${
          disabled
            ? "opacity-50 cursor-not-allowed"
            : "cursor-pointer hover:bg-white/15"
        }`}
        data-testid="preset-select"
      >
        <option value="" disabled>
          Select preset...
        </option>
        {availablePresets.map((preset: GamePreset) => (
          <option key={preset.name} value={preset.name}>
            {preset.display_name} - {preset.duration_estimate}
          </option>
        ))}
      </select>

      {/* Show description of selected preset */}
      {activePreset && (
        <p
          className="mt-2 text-sm text-gray-400"
          data-testid="preset-description"
        >
          {availablePresets.find((p) => p.name === activePreset)?.description}
        </p>
      )}

      {disabled && (
        <p className="mt-2 text-xs text-yellow-400">
          Only the host can change game settings
        </p>
      )}
    </div>
  );
}
